import { getAccessToken } from '@/lib/tokenStorage';

import { EditSemenTransactionInput, SemenTransactionDto } from '.';
import {
  AddSemenInput,
  AddSemenToShipmentInput,
  AddSemenTransactionInput,
  AddSemenTransactionV2Input,
  AddStorageLocationInput,
  EditSemenInput,
  FertilizationDto,
  RemoveSemenFromShipmentInput,
  SemenApiResponse,
  SemenPayload,
  SemenTransactionQueryDto,
  ShipmentPayload,
  StorageLocationPayload,
} from './types';

async function fetchSemen(
  searchParams: URLSearchParams = new URLSearchParams(),
  useV3: boolean = false,
): Promise<SemenApiResponse> {
  const queryString = searchParams.toString();

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  const endpoint = useV3 ? '/api/v3/semen' : '/api/v2/semen';
  const url = `${API_BASE_URL}${endpoint}${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

async function addSemen(input: AddSemenInput): Promise<SemenPayload> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not defined');
  }
  const url = `${API_BASE_URL}/api/semen`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

async function editSemen(inputs: EditSemenInput[]): Promise<SemenPayload[]> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not defined');
  }
  const url = `${API_BASE_URL}/api/semen`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  try {
    const response = await fetch(url, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(inputs),
    });

    if (!response.ok && response.status !== 400) {
      const errorText = await response.text();
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`,
      );
    }

    const results = await response.json();
    return results;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error(
        `Network error: Unable to connect to ${url}. Please ensure the API server is running.`,
        { cause: error },
      );
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Unknown error occurred while editing semen', {
      cause: error,
    });
  }
}

async function fetchSemenTransactionsV2(
  searchParams: URLSearchParams = new URLSearchParams(),
): Promise<SemenApiResponse> {
  const queryString = searchParams.toString();

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  const url = `${API_BASE_URL}/api/v2/semen/semen-transactions${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

async function fetchSemenTransactions(
  query: SemenTransactionQueryDto = {},
): Promise<SemenTransactionDto[]> {
  const searchParams = new URLSearchParams();

  if (query.semenId) {
    searchParams.append('semenId', query.semenId);
  }
  if (query.date) {
    searchParams.append('date', query.date);
  }
  if (query.name) {
    searchParams.append('name', query.name);
  }
  if (query.transactionTypeName) {
    searchParams.append('transactionTypeName', query.transactionTypeName);
  }
  if (query.quantity !== undefined) {
    searchParams.append('quantity', query.quantity.toString());
  }
  if (query.sireName) {
    searchParams.append('sireName', query.sireName);
  }

  if (query.startRow) {
    searchParams.append('startRow', query.startRow.toString());
  }
  if (query.endRow) {
    searchParams.append('endRow', query.endRow.toString());
  }

  if (query.orderBy) {
    searchParams.append('orderBy', query.orderBy);
  }

  const queryString = searchParams.toString();

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  const url = `${API_BASE_URL}/api/semen/semen-transactions${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 204) {
    return [];
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const responseData = await response.json();
  return responseData as SemenTransactionDto[];
}

async function addSemenTransactionV2(
  input: AddSemenTransactionV2Input,
): Promise<SemenPayload> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not defined');
  }
  const url = `${API_BASE_URL}/api/v2/semen/semen-transaction`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

async function addSemenTransaction(
  input: AddSemenTransactionInput,
): Promise<SemenPayload> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not defined');
  }
  const url = `${API_BASE_URL}/api/semen/semen-transaction`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

async function editSemenTransaction(
  semenTransactionId: string,
  input: EditSemenTransactionInput,
): Promise<SemenPayload> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not defined');
  }
  const url = `${API_BASE_URL}/api/semen/semen-transaction/${semenTransactionId}`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const response = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

async function fetchFertilizationsBySemenId(
  semenId: string,
): Promise<FertilizationDto[]> {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  const url = `${API_BASE_URL}/api/semen/${semenId}/fertilizations`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 204) {
    return [];
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const responseData = await response.json();
  return responseData as FertilizationDto[];
}

async function addStorageLocation(
  input: AddStorageLocationInput,
): Promise<StorageLocationPayload> {
  const url = `${import.meta.env.VITE_API_BASE_URL}/api/semen/addStorageLocation`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

async function addSemenToShipment(
  input: AddSemenToShipmentInput,
): Promise<ShipmentPayload> {
  const url = `${import.meta.env.VITE_API_BASE_URL}/api/semen/add-semen-to-shipment`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

async function removeSemenFromShipment(
  input: RemoveSemenFromShipmentInput,
): Promise<ShipmentPayload> {
  const url = `${import.meta.env.VITE_API_BASE_URL}/api/semen/remove-semen-from-shipment`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

export const semenApi = {
  getSemen: fetchSemen,
  getSemenTransactionsBySemenId: fetchSemenTransactions,
  getSemenTransactionsBySemenIdV2: fetchSemenTransactionsV2,
  addSemen,
  editSemen,
  addSemenTransaction,
  addSemenTransactionV2,
  editSemenTransaction,
  getFertilizationsBySemenId: fetchFertilizationsBySemenId,
  addStorageLocation,
  addSemenToShipment,
  removeSemenFromShipment,
};
